import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from "@st-marketplace/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

const filterSchema = () =>
	z.object({
		ip_address: z.string().or(z.literal("")),
		port: z.number().min(0).max(65535).nullable().optional(),
	});
export type FilterFormData = z.infer<ReturnType<typeof filterSchema>>;
interface FilterFormProps {
	onSearch: (values: FilterFormData) => void;
	defaultValues?: FilterFormData;
}

export const ProxiesFilterForm: React.FC<FilterFormProps> = ({
	onSearch,
	defaultValues,
}) => {
	const { t } = useTranslation();

	const form = useForm<FilterFormData>({
		resolver: zodResolver(filterSchema()),
		defaultValues: defaultValues ?? { ip_address: "", port: null },
	});
	const { onFiltersChange } = useAutoApplyFilters<FilterFormData>({
		initialFilters: defaultValues ?? { ip_address: "", port: null },
		onApply: (values) => {
			const parsed = filterSchema().safeParse(values);
			if (parsed.success) onSearch(parsed.data);
		},
		textFilterKeys: ["ip_address", "port"],
	});

	return (
		<Form {...form}>
			<div className="grid grid-cols-12 gap-4 items-end">
				<div className="col-span-12 md:col-span-4">
					<FormField
						control={form.control}
						name="ip_address"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("proxy.filterIPAddress")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										onChange={(event) => {
											field.onChange(event);
											onFiltersChange({ ip_address: event.target.value });
										}}
										ltr
										placeholder={t("proxy.filterIPAddress")}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-4">
					<FormField
						control={form.control}
						name="port"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("proxy.filterPort")}</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder={t("proxy.filterPort")}
										value={field.value ?? ""}
										onChange={(e) => {
											const value = e.target.value
												? Number(e.target.value)
												: null;
											form.setValue("port", value, {
												shouldValidate: true,
											});
											onFiltersChange({ port: value });
										}}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			</div>
		</Form>
	);
};
